import React, { useCallback } from 'react'
import { getBridgeFormResults } from '../../../api/bridge'
import { useBridgeList } from './useBridgeList'
import { formatDate, formatDateTime } from './dateRange'
import BridgeTable from './bridgeTable'
import styles from './bridge.module.css'

const groupByForm = (records) => {
  const groups = new Map()
  ;(records || []).forEach((record) => {
    const key = record.form_id != null ? String(record.form_id) : `form-${record.form_name || 'unknown'}`
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key).push(record)
  })
  return Array.from(groups.entries())
}

export default function FormResultsTab({ personId, dateRange }) {
  const fetcher = useCallback(
    (skip, limit) =>
      getBridgeFormResults({
        personId,
        startDate: formatDate(dateRange[0]),
        endDate: formatDate(dateRange[1]),
        skip,
        limit
      }),
    [personId, dateRange]
  )
  const { data, count, loading, page, setPage, error } = useBridgeList(
    fetcher,
    [personId, dateRange]
  )

  const columns = [
    {
      title: 'Recorded',
      dataIndex: 'recorded_at',
      width: 140,
      render: formatDateTime
    },
    { title: 'Question', dataIndex: 'question', render: (v) => v || '—' },
    { title: 'Parameter', dataIndex: 'parameter_name', render: (v) => v || '—' },
    {
      title: 'Value',
      key: 'value',
      width: 120,
      render: (_, record) => {
        const value =
          record.int_value != null
            ? record.int_value
            : record.string_value != null
              ? record.string_value
              : record.text_value != null
                ? record.text_value
                : record.date_value
        return value == null ? '—' : value
      }
    },
    { title: 'Unit', dataIndex: 'unit_used', width: 80, render: (v) => v || '—' }
  ]

  if (loading) {
    return (
      <div className={styles.tableWrap}>
        <BridgeTable
          columns={columns}
          dataSource={[]}
          loading
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          emptyText={error ? `Failed to load: ${error}` : 'No form results'}
        />
      </div>
    )
  }

  const groups = groupByForm(data)

  if (groups.length === 0) {
    return (
      <div className={styles.tableWrap}>
        <BridgeTable
          columns={columns}
          dataSource={[]}
          loading={false}
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          emptyText={error ? `Failed to load: ${error}` : 'No form results'}
        />
      </div>
    )
  }

  return (
    <div className={styles.tableWrap}>
      {groups.map(([formId, records]) => {
        const formName = (records[0] && records[0].form_name) || `Form #${formId}`
        return (
          <div key={formId} className={styles.groupWrap}>
            <div className={styles.groupHeader}>
              <span>{formName}</span>
              <span className={styles.groupCount}>
                {records.length} response{records.length === 1 ? '' : 's'}
              </span>
            </div>
            <BridgeTable
              columns={columns}
              dataSource={records}
              loading={false}
              count={records.length}
              page={1}
              onPageChange={() => {}}
            />
          </div>
        )
      })}
    </div>
  )
}
